import {
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  YdsButtonComponent,
  YdsCheckboxComponent,
  YdsDropdownComponent,
  YdsModalComponent,
  YdsRadioGroupComponent,
  YdsTextFieldComponent,
  type YdsDropdownOption,
  type YdsRadioOption,
} from '../../../yds-angular';
import { isInvalid } from '../../../shared/general-ledger-yds.utils';
import { ExportRule, SelectionCodeOption } from '../../../model/export-rule.model';
import { ExportRuleService } from '../export-rule.service';
import { exportLineLabel } from '../exportdefinition-shared';

/**
 * "Exportregel toevoegen / wijzigen" popup. Reused from both the list page split
 * button and the export definition editable page. Performs the save itself and
 * emits {@link saved} so the caller can refresh.
 */
@Component({
  selector: 'app-exportrule-dialog',
  imports: [
    ReactiveFormsModule,
    YdsModalComponent,
    YdsButtonComponent,
    YdsTextFieldComponent,
    YdsRadioGroupComponent,
    YdsDropdownComponent,
    YdsCheckboxComponent,
  ],
  templateUrl: './exportrule-dialog.component.html',
  styleUrl: './exportrule-dialog.component.scss',
})
export class ExportruleDialogComponent {
  readonly isInvalid = isInvalid;

  /** Two-way open state so the caller can show/hide the popup. */
  readonly open = model<boolean>(false);

  /** Export definition the rule belongs to. */
  readonly exportDefinitionId = input<string>('');

  /** Selection lists (fetched once by the caller and passed in). */
  readonly kindOptions = input<SelectionCodeOption[]>([]);
  readonly headerLevelOptions = input<SelectionCodeOption[]>([]);

  /** Rule being edited, or null when adding a new rule. */
  readonly editRule = input<ExportRule | null>(null);

  /** Line kinds already used by other rules; those radio options are disabled. */
  readonly existingKinds = input<string[]>([]);

  /** Pre-selected line kind when adding a rule from a "Voeg ... toe" button. */
  readonly presetKind = input<string>('');

  /** Emitted after a successful add/update so the caller can reload. */
  readonly saved = output<void>();

  private formBuilder = inject(FormBuilder);
  private exportRuleService = inject(ExportRuleService);
  private destroyRef = inject(DestroyRef);

  readonly isSaving = signal(false);
  readonly validationTriggered = signal(false);

  readonly form: FormGroup = this.formBuilder.group({
    exportRuleName: ['', Validators.required],
    kindOfExportLine: ['', Validators.required],
    headerLevel: ['-'],
    applyItemSeparator: [false],
    applyLineSeparator: [false],
  });

  readonly isEditMode = computed(() => this.editRule() !== null);

  readonly title = computed(() =>
    this.isEditMode() ? 'Exportregel wijzigen' : 'Exportregel toevoegen',
  );

  /** Radio options for "Soort regel", disabling kinds already in use. */
  readonly kindRadioOptions = computed<YdsRadioOption[]>(() => {
    const currentKind = this.editRule()?.kindOfExportLine ?? null;
    const used = new Set(this.existingKinds().filter((code) => code !== currentKind));
    return this.kindOptions().map((option) => ({
      id: option.code,
      label: exportLineLabel(option.code, option.description),
      disabled: used.has(option.code),
    }));
  });

  /** Dropdown options for "Niveau kopregel". */
  readonly headerLevelDropdownOptions = computed<YdsDropdownOption[]>(() =>
    this.headerLevelOptions().map((option) => ({
      id: option.code,
      label: option.description || option.code,
    })),
  );

  constructor() {
    // Re-seed the form each time the popup opens (add) or the edited rule changes.
    effect(() => {
      const isOpen = this.open();
      const rule = this.editRule();
      if (!isOpen) {
        return;
      }
      this.validationTriggered.set(false);
      if (rule) {
        this.form.reset({
          exportRuleName: rule.exportRuleName,
          kindOfExportLine: rule.kindOfExportLine,
          headerLevel: rule.headerLevel || '-',
          applyItemSeparator: rule.applyItemSeparator,
          applyLineSeparator: rule.applyLineSeparator,
        });
      } else {
        this.form.reset({
          exportRuleName: '',
          kindOfExportLine: this.presetKind() || '',
          headerLevel: '-',
          applyItemSeparator: false,
          applyLineSeparator: false,
        });
      }
    });
  }

  onCancel(): void {
    this.open.set(false);
  }

  onSave(): void {
    if (this.form.invalid) {
      this.validationTriggered.set(true);
      this.form.markAllAsTouched();
      return;
    }

    const definitionId = this.exportDefinitionId();
    if (!definitionId) {
      return;
    }

    const value = this.form.value as {
      exportRuleName: string;
      kindOfExportLine: string;
      headerLevel: string;
      applyItemSeparator: boolean;
      applyLineSeparator: boolean;
    };

    const existing = this.editRule();
    const rule: ExportRule = {
      exportRuleId: existing?.exportRuleId ?? '',
      exportRuleName: value.exportRuleName,
      exportDefinitionId: definitionId,
      kindOfExportLine: value.kindOfExportLine,
      headerLevel: value.headerLevel || '-',
      applyItemSeparator: value.applyItemSeparator,
      applyLineSeparator: value.applyLineSeparator,
      lineReference: existing?.lineReference ?? null,
    };

    this.isSaving.set(true);

    const save$ = existing
      ? this.exportRuleService.updateExportRule(existing.exportRuleId, rule)
      : this.exportRuleService.addExportRule(rule);

    save$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.open.set(false);
        this.saved.emit();
      },
      error: () => {
        this.isSaving.set(false);
      },
    });
  }
}
